import { Client } from '@/types/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusBadge, BooleanBadge } from '@/components/ui/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, 
  User, 
  Mail, 
  FileText, 
  Calculator, 
  Wallet,
  BookOpen,
  MessageSquare
} from 'lucide-react';

interface ClientDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | null;
}

export function ClientDetailModal({ open, onOpenChange, client }: ClientDetailModalProps) {
  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-primary flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {client.companyName}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-100px)]">
          <div className="space-y-6 pr-4">
            {/* Header Info */}
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-7 w-7 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{client.directorName}</h3>
                <p className="text-muted-foreground">{client.natureOfBusiness}</p>
                <Badge variant="secondary" className="mt-1">{client.clientType}</Badge>
              </div>
              <div className="text-right">
                <span className="text-xs text-muted-foreground">Client #</span>
                <p className="font-mono font-semibold">{client.srNo.toString().padStart(4, '0')}</p>
              </div>
            </div>

            {/* Tax Information */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Tax Information
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">ABN</span>
                  <p className="font-mono font-medium">{client.abn || '—'}</p>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">TFN</span>
                  <p className="font-mono font-medium">{client.tfn || '—'}</p>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">GST Registered</span>
                  <div className="mt-1">
                    <BooleanBadge value={client.gstFilling} />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Contact & Team */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Contact & Assignment
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-card rounded-lg border col-span-1">
                  <span className="text-xs text-muted-foreground">Email</span>
                  <a href={`mailto:${client.emailAddress}`} className="text-info hover:underline block truncate">
                    {client.emailAddress || '—'}
                  </a>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Team</span>
                  <p className="font-medium">{client.team || '—'}</p>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Employee</span>
                  <p className="font-medium">{client.employee || '—'}</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* BAS & Compliance */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                BAS & Compliance
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Statement</span>
                  <div className="mt-1">
                    <BooleanBadge value={client.statementReceived} trueLabel="Received" falseLabel="Pending" />
                  </div>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">BAS Status</span>
                  <div className="mt-1">
                    <StatusBadge status={client.basStatus} />
                  </div>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">BAS Return</span>
                  <div className="mt-1">
                    <StatusBadge status={client.basReturn} />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Payroll & Super */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                Payroll & Superannuation
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Payroll / Payslip</span>
                  <div className="mt-1">
                    <StatusBadge status={client.payrollPayslip} />
                  </div>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">STP File</span>
                  <div className="mt-1">
                    <StatusBadge status={client.stpFile} />
                  </div>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Super Payment</span>
                  <div className="mt-1">
                    <StatusBadge status={client.superPayment} />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Bookkeeping */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Bookkeeping
              </h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Software</span>
                  <p className="font-medium">{client.bookkeepingSoftware}</p>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Handy Ledger</span>
                  <div className="mt-1">
                    <BooleanBadge value={client.handyLedger} />
                  </div>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Status</span>
                  <div className="mt-1">
                    <StatusBadge status={client.bookkeepingStatus} />
                  </div>
                </div>
                <div className="p-3 bg-card rounded-lg border">
                  <span className="text-xs text-muted-foreground">Invoices</span>
                  <div className="mt-1">
                    <StatusBadge status={client.invoices} />
                  </div>
                </div>
              </div>
            </div>

            {/* Remarks */}
            {client.remarks && (
              <>
                <Separator />
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Remarks
                  </h4>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm">{client.remarks}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
